import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { BadgeCheck } from 'lucide-react'
import { useAuth } from '@/shared/hooks/useAuth'
import { useProfile, useVerifyMaxPhone } from '@/shared/hooks/useProfile'
import { bridge } from '@/shared/bridge'
import { apiClient } from '@/shared/api/client'

const AUTH_TOKEN_KEY = 'maximur_auth_token'

export function Settings() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user, profile, logout, initialize, refreshProfile } = useAuth()
  const { data: fullProfile, isLoading: profileLoading, isFetching: profileFetching } = useProfile(
    user?.id ?? null,
  )
  const verifyMaxPhone = useVerifyMaxPhone()
  const [isDarkMode, setIsDarkMode] = useState(
    document.documentElement.classList.contains('dark'),
  )
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [phoneBusy, setPhoneBusy] = useState(false)
  const [phoneError, setPhoneError] = useState<string | null>(null)

  /** Если JWT есть, а profile в zustand потерялся — подтянуть с API, иначе роутер ведёт себя как без анкеты. */
  useEffect(() => {
    if (!user?.id || profile) return
    void refreshProfile().catch(() => {})
  }, [user?.id, profile, refreshProfile])

  const phoneVerified = Boolean(fullProfile?.isPhoneVerified ?? user?.isPhoneVerified)
  const phoneMaskedDisplay = fullProfile?.phoneMasked ?? user?.phone ?? null

  const handleToggleDarkMode = () => {
    setIsDarkMode(!isDarkMode)
    if (isDarkMode) {
      document.documentElement.classList.remove('dark')
      document.documentElement.classList.add('light')
      localStorage.setItem('theme', 'light')
    } else {
      document.documentElement.classList.add('dark')
      document.documentElement.classList.remove('light')
      localStorage.setItem('theme', 'dark')
    }
  }

  const handleExportData = async () => {
    try {
      const data = {
        user: {
          id: user?.id,
          maxUserId: user?.maxUserId,
        },
        profile: fullProfile ?? profile,
        exportedAt: new Date().toISOString(),
      }

      const json = JSON.stringify(data, null, 2)
      const blob = new Blob([json], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `tolko-svoi-export-${new Date().getTime()}.json`
      a.click()
      URL.revokeObjectURL(url)

      bridge.haptic('success')
    } catch (error) {
      console.error('Export failed:', error)
      bridge.haptic('error')
    }
  }

  const handleDeleteAccount = async () => {
    setIsDeleting(true)
    try {
      await apiClient.delete<{ ok: boolean }>('/api/profile/me')
      queryClient.removeQueries({ queryKey: ['profile'] })
      queryClient.removeQueries({ queryKey: ['feed'] })
      queryClient.removeQueries({ queryKey: ['matches'] })
      logout()
      await initialize()
      navigate('/onboarding', { replace: true })
      bridge.haptic('success')
    } catch (error) {
      console.error('Delete failed:', error)
      bridge.haptic('error')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleRefreshSession = async () => {
    localStorage.removeItem(AUTH_TOKEN_KEY)
    apiClient.clearToken()
    await initialize()
    navigate('/dashboard')
  }

  const handleVerifyPhoneFromMax = async () => {
    setPhoneError(null)
    setPhoneBusy(true)
    try {
      const { phone } = await bridge.requestContact()
      if (!phone?.replace(/\D/g, '')) {
        throw new Error('Номер не получен. Согласись на доступ к контакту в MAX или попробуй снова.')
      }
      await verifyMaxPhone.mutateAsync(phone)
      await queryClient.invalidateQueries({ queryKey: ['profile', user?.id ?? null] })
      await refreshProfile()
      bridge.haptic('success')
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      if (/requestContact not available/i.test(msg)) {
        setPhoneError('Доступно в приложении MAX: открой мини-приложение из MAX, не из обычного браузера.')
      } else {
        setPhoneError(msg.replace(/^\[\d+\]\s*/, ''))
      }
      bridge.haptic('error')
    } finally {
      setPhoneBusy(false)
    }
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 flex flex-col">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-gray-700 p-4 sticky top-0 bg-white dark:bg-gray-900 z-10">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Настройки</h1>
          <button
            onClick={() => navigate('/dashboard')}
            className="text-gray-600 dark:text-gray-400 text-xl"
          >
            ←
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Profile Section */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Мой профиль
          </h2>
          <div className="space-y-3">
            {profileLoading && !fullProfile ? (
              <div className="h-14 rounded-lg bg-gray-100 dark:bg-gray-800 animate-pulse" />
            ) : (
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg gap-3">
                <div className="min-w-0 flex-1">
                  <span className="text-gray-700 dark:text-gray-300 font-medium block truncate">
                    {fullProfile?.name ?? profile?.name ?? '—'}
                  </span>
                  {fullProfile?.city ? (
                    <span className="text-xs text-gray-500 dark:text-gray-400 block truncate">
                      {fullProfile.city}
                    </span>
                  ) : null}
                  {fullProfile?.interests?.length ? (
                    <span className="text-xs text-gray-500 dark:text-gray-400 block mt-1 line-clamp-2">
                      Интересы:{' '}
                      {fullProfile.interests
                        .map((row: { interest?: { title?: string } }) => row.interest?.title)
                        .filter(Boolean)
                        .join(', ')}
                    </span>
                  ) : null}
                  {profileFetching && fullProfile ? (
                    <span className="text-[10px] text-gray-400 mt-1">Обновляем…</span>
                  ) : null}
                </div>
                <button
                  onClick={() => navigate('/profile-setup')}
                  className="text-accent text-sm font-semibold shrink-0"
                >
                  Изменить
                </button>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => navigate('/settings/preview')}
            className="mt-3 w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-left text-sm font-semibold text-gray-900 shadow-sm transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700/80"
          >
            Как меня видят другие
            <span className="mt-0.5 block text-xs font-normal text-gray-500 dark:text-gray-400">
              Откроется анкета как в ленте; свайп в любую сторону — обратно в настройки
            </span>
          </button>
        </div>

        {/* Phone verification (MAX requestContact) */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Телефон</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
            Подтверждение через MAX: номер не вводится вручную. В ленте у тебя появится значок доверия.
          </p>
          {phoneVerified ? (
            <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50/90 p-4 dark:border-emerald-800/60 dark:bg-emerald-950/40">
              <BadgeCheck className="h-6 w-6 shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden />
              <div className="min-w-0">
                <p className="font-semibold text-emerald-900 dark:text-emerald-100">Номер подтверждён в MAX</p>
                {phoneMaskedDisplay ? (
                  <p className="mt-1 text-sm text-emerald-800/90 dark:text-emerald-200/90">{phoneMaskedDisplay}</p>
                ) : null}
                <p className="mt-2 text-xs text-emerald-800/80 dark:text-emerald-300/80">
                  Другие видят значок верификации рядом с твоим именем в анкете в ленте.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <button
                type="button"
                disabled={phoneBusy || verifyMaxPhone.isPending || profileLoading}
                onClick={() => {
                  void handleVerifyPhoneFromMax()
                }}
                className="w-full rounded-xl bg-accent px-4 py-3 text-center text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {phoneBusy || verifyMaxPhone.isPending ? 'Запрос в MAX…' : 'Подтвердить номер через MAX'}
              </button>
              {phoneError ? (
                <p className="text-sm text-red-600 dark:text-red-400" role="alert">
                  {phoneError}
                </p>
              ) : null}
            </div>
          )}
        </div>

        {/* Appearance */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Внешний вид
          </h2>
          <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="flex items-center gap-2">
              {isDarkMode ? '🌙' : '☀️'}
              <span className="text-gray-700 dark:text-gray-300">
                {isDarkMode ? 'Тёмный режим' : 'Светлый режим'}
              </span>
            </div>
            <button
              onClick={handleToggleDarkMode}
              className="relative inline-flex h-6 w-11 items-center rounded-full bg-gray-300 dark:bg-gray-600 transition-colors"
              style={{
                backgroundColor: isDarkMode ? '#6366f1' : '#d1d5db',
              }}
            >
              <span
                className="inline-block h-4 w-4 transform rounded-full bg-white transition-transform"
                style={{
                  transform: isDarkMode ? 'translateX(22px)' : 'translateX(2px)',
                }}
              />
            </button>
          </div>
        </div>

        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Уведомления в MAX</h2>
          <div className="space-y-2 rounded-lg bg-gray-50 p-3 text-sm text-gray-600 dark:bg-gray-800/80 dark:text-gray-300">
            <p>
              Иногда о новом сообщении в чате или о мэтче приходит короткое сообщение в чат с{' '}
              <strong className="text-gray-800 dark:text-gray-100">ботом приложения в MAX</strong> — не внутри
              мини-приложения, а как обычное личное сообщение, часто с кнопкой «Открыть».
            </p>
            <p>
              Если таких сообщений нет, всё равно можно пользоваться лентой и чатами здесь — они не зависят от
              напоминаний в мессенджере.
            </p>
          </div>
        </div>

        {/* Data */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Мои данные
          </h2>
          <button
            onClick={handleExportData}
            className="w-full bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white px-4 py-3 rounded-lg font-semibold hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors mb-3"
          >
            📥 Экспортировать как JSON
          </button>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Скачай все свои данные профиля в формате JSON для резервной копии или переноса.
          </p>
        </div>

        {/* Account */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Аккаунт
          </h2>
          <button
            onClick={() => {
              handleRefreshSession().catch((err) => {
                console.error('Session refresh failed:', err)
                bridge.haptic('error')
              })
            }}
            className="w-full bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white px-4 py-3 rounded-lg font-semibold hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors mb-3"
          >
            🔄 Обновить сессию
          </button>

          {!showDeleteConfirm ? (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="w-full bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 px-4 py-3 rounded-lg font-semibold hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
            >
              🗑️ Удалить аккаунт
            </button>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-red-600 dark:text-red-400 font-semibold">
                ⚠️ Это действие необратимо. Все данные будут удалены.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white px-4 py-2 rounded-lg font-semibold"
                >
                  Отмена
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={isDeleting}
                  className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-red-700 disabled:opacity-50"
                >
                  {isDeleting ? 'Удаляю...' : 'Удалить'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 text-center text-xs text-gray-500 dark:text-gray-400">
          <p className="text-gray-600 dark:text-gray-300">ТолькоСвои</p>
          <p>Знакомства в MAX</p>
        </div>
      </div>
    </div>
  )
}
